import type { PaidPlanId } from "@cva/shared";
import { authClient } from "./auth-client";

/**
 * Stripe through Better Auth's plugin (docs/16-geschaeftsmodell.md). Both calls
 * answer with a Stripe page to go to: checkout for a new subscription (or the
 * portal's confirmation for a plan change), the customer portal otherwise.
 */
const planUrl = (orgId: string, extra = "") =>
  `${window.location.origin}/o/${orgId}/settings?tab=plan${extra}`;

/** The plugin answers in English; the codes we can hit from this page, in German. */
const MESSAGES: Record<string, string> = {
  UNAUTHORIZED: "Den Tarif kann nur der Owner der Organisation buchen oder ändern.",
  ALREADY_SUBSCRIBED_PLAN: "Diesen Tarif hat die Organisation schon.",
  CUSTOMER_NOT_FOUND: "Für diese Organisation gibt es noch kein Abo.",
  SUBSCRIPTION_NOT_FOUND: "Das Abo wurde nicht gefunden.",
  EMAIL_VERIFICATION_REQUIRED: "Bitte bestätige zuerst deine E-Mail-Adresse.",
  CONSENT_REQUIRED: "Bitte bestätige zuerst die Bestellübersicht.",
};
const FALLBACK = "Stripe ist gerade nicht erreichbar, bitte versuche es später noch einmal.";

async function go(path: string, body: Record<string, unknown>): Promise<void> {
  const { data, error } = await authClient.$fetch<{ url?: string }>(path, {
    method: "POST",
    body: { ...body, customerType: "organization", disableRedirect: true },
  });
  if (error || !data?.url) {
    const code = (error as { code?: string } | null)?.code;
    // Stripe's own reason (English) for whoever looks into it.
    console.error("Stripe:", error ?? data);
    throw new Error((code && MESSAGES[code]) || FALLBACK);
  }
  window.location.href = data.url;
}

/** New subscription (with the free trial, if still available) or change of plan. */
export const choosePlan = (orgId: string, plan: PaidPlanId) =>
  go("/subscription/upgrade", {
    plan,
    referenceId: orgId,
    locale: "de",
    successUrl: planUrl(orgId, "&checkout=done"),
    cancelUrl: planUrl(orgId),
    returnUrl: planUrl(orgId),
  });

/** Stripe's customer portal: payment method, invoices, cancel. */
export const openBillingPortal = (orgId: string) =>
  go("/subscription/billing-portal", {
    referenceId: orgId,
    locale: "de",
    returnUrl: planUrl(orgId),
  });
